import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  Paperclip,
  Image as ImageIcon,
  Lightbulb,
  Telescope,
  BookOpen,
  Globe,
  Palette,
  Mic,
  ArrowUp,
  X,
  MessageSquare,
  Bot
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { deepgramService, TranscriptionResult } from '@/services/deepgramService';
import { useToast } from '@/hooks/use-toast';
import { AttachedMedia, AIAgent } from '@/types/aiAgents';
import { AgentDropdown } from './AgentDropdown';
import { aiAgents } from '@/services/aiAgentServices';

const byPrefixAndName = {
  fas: {
    plus: faPlus,
  },
} as const;

interface DiscoverInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isDarkMode: boolean;
  onModeChange?: (mode: 'chat' | 'image' | 'web' | 'deep-research') => void;
  isLoading?: boolean;
  onMediaAttach?: (media: AttachedMedia | null) => void;
  attachedMedia?: AttachedMedia | null;
  selectedMode?: 'chat' | 'image' | 'web' | 'deep-research';
  selectedAgent?: AIAgent | null;
  onAgentChange?: (agent: AIAgent | null) => void;
}

export const DiscoverInput: React.FC<DiscoverInputProps> = ({
  value,
  onChange,
  onSend,
  isDarkMode,
  onModeChange,
  isLoading = false,
  onMediaAttach,
  attachedMedia,
  selectedMode = 'chat',
  selectedAgent = null,
  onAgentChange,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [localMedia, setLocalMedia] = useState<AttachedMedia | null>(attachedMedia ?? null);
  const menuRef = useRef<HTMLDivElement>(null);
  const agentDropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [recordedTranscript, setRecordedTranscript] = useState<string>('');
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('top');
  const finalTranscriptRef = useRef<string>('');
  const recordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();

  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_DOC_SIZE = 300 * 1024; // 300KB
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const ALLOWED_DOC_TYPES = ['application/pdf'];

  const ModeIcon = useMemo(() => {
    const modeIconMap: Record<typeof selectedMode, React.ComponentType<{ className?: string }>> = {
      'chat': MessageSquare,
      'image': ImageIcon,
      'web': Globe,
      'deep-research': Telescope,
    };
    return modeIconMap[selectedMode] || MessageSquare;
  }, [selectedMode]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    if (!showMenu) return;

    const adjustPosition = () => {
      const menuEl = menuRef.current;
      const triggerEl = triggerRef.current;
      if (!menuEl || !triggerEl) return;

      const triggerRect = triggerEl.getBoundingClientRect();
      const menuRect = menuEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const willOverflowTop = triggerRect.top - menuRect.height - 8 < 0;
      const willOverflowBottom = triggerRect.bottom + menuRect.height + 8 > viewportHeight;

      if (willOverflowTop && !willOverflowBottom) {
        setMenuPosition('bottom');
      } else if (willOverflowBottom && !willOverflowTop) {
        setMenuPosition('top');
      } else if (spaceBelow >= spaceAbove) {
        setMenuPosition('bottom');
      } else {
        setMenuPosition('top');
      }
    };

    const handleDeferredAdjust = () => requestAnimationFrame(adjustPosition);

    handleDeferredAdjust();

    window.addEventListener('resize', adjustPosition);
    window.addEventListener('scroll', adjustPosition, true);

    return () => {
      window.removeEventListener('resize', adjustPosition);
      window.removeEventListener('scroll', adjustPosition, true);
    };
  }, [showMenu]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !isLoading) {
        onSend();
      }
    }
  };

  const clearRecordingTimeout = useCallback(() => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearRecordingTimeout();
    };
  }, [clearRecordingTimeout]);

  const startRecording = async () => {
    try {
      // Check if browser supports required APIs
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('getUserMedia not supported');
        setTranscriptionError('Audio recording not supported in this browser');
        return;
      }

      // Get microphone access for waveform visualization
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();

      setMediaStream(stream);
      setAudioContext(audioCtx);
      setIsRecording(true);
      setRecordedTranscript('');
      setTranscriptionError(null);
      finalTranscriptRef.current = '';
      clearRecordingTimeout();

      // Start Deepgram transcription
      await deepgramService.startRecording(
        (result: TranscriptionResult) => {
          // Handle transcription results
          if (result.isFinal) {
            // Append final transcript
            finalTranscriptRef.current += (finalTranscriptRef.current ? ' ' : '') + result.transcript;
            setRecordedTranscript(finalTranscriptRef.current);
          } else {
            // Show interim results
            const interim = finalTranscriptRef.current + (finalTranscriptRef.current ? ' ' : '') + result.transcript;
            setRecordedTranscript(interim);
          }
          setTranscriptionError(null);
        },
        (error: string) => {
          console.error('Transcription error:');
          setTranscriptionError(error);
        }
      );

      recordingTimeoutRef.current = setTimeout(() => {
        setTranscriptionError('Recording limit reached (50s)');
        stopRecording();
      }, 50000);
    } catch (error) {
      console.error('Error starting recording:');
      setIsRecording(false);
      setTranscriptionError('Could not start recording');

      clearRecordingTimeout();

      // Cleanup on error
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      }
      if (audioContext) {
        audioContext.close();
        setAudioContext(null);
      }
    }
  };

  const stopRecording = () => {
    try {
      clearRecordingTimeout();

      // Stop Deepgram service
      deepgramService.stopRecording();

      // Preserve the final transcript before any state changes
      const finalTranscript = finalTranscriptRef.current.trim();

      // Cleanup media stream
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      }

      // Cleanup audio context
      if (audioContext) {
        audioContext.close();
        setAudioContext(null);
      }

      // Reset recording state immediately
      setIsRecording(false);

      // Insert the final transcript into the input AFTER resetting recording state
      // Use setTimeout to ensure state updates are processed
      setTimeout(() => {
        if (finalTranscript && finalTranscript.length > 0) {
          onChange(value + (value ? ' ' : '') + finalTranscript);
        } else if (!transcriptionError) {
          console.warn('No transcript available');
        }

        // Clear transcript states after inserting into input
        setRecordedTranscript('');
        finalTranscriptRef.current = '';
      }, 0);
    } catch (error) {
      console.error('Error stopping recording:');
    }
  };

  useEffect(() => {
    setLocalMedia(attachedMedia ?? null);
  }, [attachedMedia]);

  const createAttachedMedia = (file: File, type: AttachedMedia['type']): AttachedMedia => {
    const url = URL.createObjectURL(file);
    return {
      type,
      url,
      name: file.name,
      file,
    };
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setLocalMedia(null);
      onMediaAttach?.(null);
      return;
    }

    const fileType = file.type?.toLowerCase() || '';
    const fileName = file.name?.toLowerCase() || '';
    const isImage = ALLOWED_IMAGE_TYPES.includes(fileType) || ['.jpg', '.jpeg', '.png', '.webp', '.gif'].some((ext) => fileName.endsWith(ext));
    const isDocument = ALLOWED_DOC_TYPES.includes(fileType) || fileName.endsWith('.pdf');

    if (isImage) {
      if (file.size > MAX_IMAGE_SIZE) {
        toast({
          title: 'Image too large',
          description: 'Please upload images up to 5 MB.',
          variant: 'destructive',
        });
        fileInputRef.current && (fileInputRef.current.value = '');
        return;
      }
    } else if (isDocument) {
      if (file.size > MAX_DOC_SIZE) {
        toast({
          title: 'Document too large',
          description: 'Please upload documents up to 300 KB.',
          variant: 'destructive',
        });
        fileInputRef.current && (fileInputRef.current.value = '');
        return;
      }
    } else {
      toast({
        title: 'Unsupported file type',
        description: 'Upload JPG, PNG, GIF images or PDF documents.',
        variant: 'destructive',
      });
      fileInputRef.current && (fileInputRef.current.value = '');
      return;
    }

    const mediaType: AttachedMedia['type'] = isImage ? 'image' : 'document';
    const media = createAttachedMedia(file, mediaType);
    setLocalMedia(media);
    onMediaAttach?.(media);
    fileInputRef.current && (fileInputRef.current.value = '');
  };

  const clearAttachment = () => {
    setLocalMedia(null);
    onMediaAttach?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleModeSelect = (mode: 'chat' | 'image' | 'web' | 'deep-research') => {
    onModeChange?.(mode);
    setShowMenu(false);
  };

  const handleAgentSelect = (agent: AIAgent) => {
    onAgentChange?.(agent);
    setShowAgentDropdown(false);
  };

  // Debug: Log selectedAgent changes
  useEffect(() => {
  }, [selectedAgent]);

  // Close agent dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target as Node)) {
        setShowAgentDropdown(false);
      }
    };

    if (showAgentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAgentDropdown]);

  const menuItems = [
    {
      icon: Paperclip,
      label: 'Add photos & files',
      onClick: () => {
        setShowMenu(false);
        fileInputRef.current?.click();
      },
    },
    {
      icon: ImageIcon,
      label: 'Create image',
      onClick: () => handleModeSelect('image'),
      active: selectedMode === 'image',
    },
    {
      icon: Telescope,
      label: 'Deep research',
      onClick: () => handleModeSelect('deep-research'),
      active: selectedMode === 'deep-research',
    },
    {
      icon: Globe,
      label: 'Web search',
      onClick: () => handleModeSelect('web'),
      active: selectedMode === 'web',
    },
    {
      icon: Bot,
      label: 'Select agent',
      onClick: () => {
        setShowMenu(false);
        setShowAgentDropdown(true);
      },
      active: !!selectedAgent,
    },
  ];

  const isMultiline = value.includes('\n') || (inputRef.current && inputRef.current.scrollHeight > 36);
  const canSend = value.trim().length > 0 || !!localMedia;

  return (
    <div className="relative w-full max-w-4xl mx-auto px-0 sm:px-4 pb-1">
      {/* Menu Popup */}
      {showMenu && (
        <div
          ref={menuRef}
          className={cn(
            'absolute left-4 w-full max-w-[13rem] rounded-3xl shadow-2xl z-[1000] overflow-hidden',
            menuPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-0',
            isDarkMode ? 'bg-[#2a2a2a]' : 'bg-white'
          )}
        >
          <div className="flex flex-col gap-1 p-1.5 z-[100]">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  onClick={item.onClick}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-2xl transition-all duration-200 text-left',
                    isDarkMode
                      ? cn('hover:bg-white/10 text-gray-200', item.active && 'bg-white/10 text-white')
                      : cn('hover:bg-gray-100 text-gray-800', item.active && 'bg-gray-100 text-gray-900')
                  )}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-normal">{item.label}</span>
                  {item.label === 'More' && (
                    <span className="ml-auto">→</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Agent Dropdown */}
      {showAgentDropdown && (
        <div
          ref={agentDropdownRef}
          className="absolute left-4 w-full max-w-[18rem] rounded-3xl shadow-2xl z-[1000] bottom-full mb-2"
        >
          <AgentDropdown
            agents={aiAgents}
            onAgentSelect={handleAgentSelect}
            isDarkMode={isDarkMode}
            inputRef={inputRef}
            onClose={() => setShowAgentDropdown(false)}
            open={showAgentDropdown}
          />
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Image Preview Above Input */}
      {localMedia && localMedia.type === 'image' && localMedia.url && (
        <div className={cn(
          'mb-3 max-h-[70px] max-w-[100px] h-[max-content] w-[max-content] rounded-2xl overflow-hidden border',
          isDarkMode ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'
        )}>
          <div className="relative group">
            <img
              src={localMedia.url}
              alt={localMedia.name}
              className="w-full h-auto max-h-[300px] object-contain"
            />
            <button
              onClick={clearAttachment}
              className={cn(
                'absolute top-0 right-0 p-2 rounded-full transition-all duration-200',
                isDarkMode
                  ? 'bg-black/60 hover:bg-black/80 text-white'
                  : 'bg-white/80 hover:bg-white text-gray-800',
                'backdrop-blur-sm'
              )}
              aria-label="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Input Container */}
      <div
        className={cn(
          'relative shadow-lg transition-all duration-200 overflow-hidden',
          isMultiline ? 'rounded-2xl' : 'rounded-full',
          isDarkMode ? 'bg-[#2a2a2a]' : 'bg-white',
          isRecording && ''
        )}
      >
        <div
          className={cn(
            'flex gap-2 px-2 sm:px-4 py-2',
            isRecording ? 'items-center' : 'items-end'
          )}
        >
          {/* Plus Button */}
          <button
            ref={triggerRef}
            onClick={() => setShowMenu(!showMenu)}
            className={cn(
              'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200',
              isDarkMode
                ? 'hover:bg-white/10 text-gray-300'
                : 'hover:bg-gray-100 text-gray-600',
              showMenu && (isDarkMode ? 'bg-white/10' : 'bg-gray-100')
            )}
          >
            <FontAwesomeIcon
              icon={byPrefixAndName.fas['plus']}
              className={cn('w-5 h-5 transition-transform duration-200', showMenu && 'rotate-45')}
            />
          </button>
          {/* Mode Icon - Only show for non-chat modes */}
          {selectedMode !== 'chat' && (
            <button
              onClick={() => handleModeSelect('chat')}
              className={cn(
                'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 border hover:bg-white/20',
                isDarkMode
                  ? 'border-white/10 bg-white/5 text-white'
                  : 'border-gray-200 bg-gray-100 text-gray-700'
              )}
              title="Switch to chat mode"
            >
              <ModeIcon className="w-4 h-4" />
            </button>
          )}

          {/* Agent Icon - Show when agent is selected */}
          {selectedAgent && (
            <div
              className={cn(
                'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 border overflow-hidden',
                isDarkMode
                  ? 'border-white/30 bg-gradient-to-br from-blue-500/30 to-purple-500/30'
                  : 'border-blue-300 bg-blue-50'
              )}
              title={`Agent: ${selectedAgent.name}`}
            >
              <Bot className="w-4 h-4" />
              {selectedAgent.icon ? (
                <img
                  src={selectedAgent.icon}
                  alt={selectedAgent.name}
                  className="w-full h-full object-cover"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                  onLoad={() => {
                  }}
                />
              ) : (
                <Bot className="w-4 h-4" />
              )}
            </div>
          )}

          {/* Voice Recording or Input */}
          <div className="flex-1 min-w-0">
            {isRecording ? (
              <div className="w-full flex items-center min-h-[36px]">
                <div className={cn(
                  'text-base px-2 w-full',
                  isDarkMode ? 'text-white' : 'text-gray-900'
                )}>
                  {recordedTranscript || (
                    <span className={cn(
                      'italic',
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    )}>
                      Listening...
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <textarea
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything"
                rows={1}
                className={cn(
                  'w-full resize-none border-none outline-none bg-transparent text-base break-words',
                  isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400',
                  'scrollbar-hide overflow-y-auto'
                )}
                style={{ maxHeight: '200px', wordBreak: 'break-word', overflowWrap: 'break-word' }}
              />
            )}
          </div>

          {/* Voice/Send Button */}
          {isRecording ? (
            <button
              onClick={stopRecording}
              className={cn(
                "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 transition-colors"
              )}
            >
              <div className="w-3 h-3 bg-white rounded-sm" />
            </button>
          ) : canSend ? (
            <button
              onClick={onSend}
              disabled={isLoading || !canSend}
              className={cn(
                'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200',
                isDarkMode
                  ? 'bg-white text-black hover:bg-white/90'
                  : 'bg-black text-white hover:bg-gray-800',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              {localMedia && (
                <div className={cn(
                  'flex items-center gap-2 px-2 py-1.5 text-xs rounded-2xl border',
                  isDarkMode ? 'border-white/10 bg-white/5 text-white' : 'border-gray-200 bg-gray-100 text-gray-800'
                )}>
                  {localMedia.type === 'image' && localMedia.url && (
                    <img
                      src={localMedia.url}
                      alt={localMedia.name}
                      className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                    />
                  )}
                  <span className="truncate max-w-[100px] sm:max-w-[140px]">{localMedia.name}</span>
                  <button
                    onClick={clearAttachment}
                    className={cn(
                      'p-1 rounded-full transition-colors flex-shrink-0',
                      isDarkMode ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-200 text-gray-600'
                    )}
                    aria-label="Remove attachment"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={cn(
                  'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200',
                  isDarkMode
                    ? 'hover:bg-white/10 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-600'
                )}
              >
                <Mic className="w-5 h-5" />
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
